import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useTicketsRichQuery, useCreditAvailableQuery } from 'features/tickets/queries';
import { isGuestUserSession, useUserSessionContext } from 'features/session/userSessionStore';
import { useTicketSlipStore } from 'features/tickets/store';
import { getCurrencySymbol, printOdd } from 'shared/utils/common';
import { formatCurrency } from 'shared/money/formatCurrency';
import { cn } from 'lib/utils';
import type {
  RichTicket,
  RichTicketCombination,
  RichTicketSystemRow,
  RichTicketTip,
} from 'features/tickets/api';

const STATUS_STYLES: Record<string, string> = {
  WIN: 'bg-emerald-100 text-emerald-800',
  LOSS: 'bg-red-100 text-red-800',
  SOLD: 'bg-amber-100 text-amber-800',
  REFUNDED: 'bg-gray-200 text-gray-700',
  CANCELLED: 'bg-gray-200 text-gray-700',
  WAITING_REFUND: 'bg-orange-100 text-orange-700',
};

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const label: Record<string, string> = {
    WIN: t('STATUS_WIN', 'Won'),
    LOSS: t('STATUS_LOSS', 'Lost'),
    SOLD: t('STATUS_PENDING', 'Pending'),
    REFUNDED: t('STATUS_REFUNDED', 'Refunded'),
    CANCELLED: t('STATUS_CANCELLED', 'Cancelled'),
    WAITING_REFUND: t('STATUS_WAITING_REFUND', 'Refunding'),
  };
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider', STATUS_STYLES[status] ?? 'bg-muted text-muted-foreground')}>
      {label[status] ?? status}
    </span>
  );
}

function TipRow({ tip, currency, oddsFormat }: { tip: RichTicketTip; currency: string; oddsFormat: string }) {
  return (
    <div className="flex items-start justify-between gap-2 py-2 border-b border-border/50 last:border-b-0">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-700 truncate">
          {tip.event_description ?? `Event #${tip.event_id}`}
        </p>
        <p className="text-[11px] text-gray-500 truncate">
          {tip.market_type_description ? `${tip.market_type_description}: ` : ''}
          {tip.outcome_description || tip.outcome_label || `#${tip.outcome_id}`}
        </p>
      </div>
      <div className="flex items-center gap-3 shrink-0 text-xs">
        <span className="font-semibold text-gray-800">{printOdd(tip.odd, oddsFormat)}</span>
        <StatusBadge status={tip.status} />
      </div>
    </div>
  );
}

function systemRowsSummary(rows: RichTicketSystemRow[] | undefined): string {
  if (!rows || rows.length === 0) return '';
  return rows.map((row) => row.label).join(', ');
}

function formatRange(minimum: number, maximum: number, symbol: string): string {
  const min = Number(minimum || 0).toFixed(2);
  const max = Number(maximum || 0).toFixed(2);
  if (minimum === maximum) {
    return `${max}${symbol}`;
  }
  return `${min}${symbol} - ${max}${symbol}`;
}

function CombinationCard({
  combination,
  currency,
  oddsFormat,
}: {
  combination: RichTicketCombination;
  currency: string;
  oddsFormat: string;
}) {
  const { t } = useTranslation();
  const sym = getCurrencySymbol(currency);

  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-foreground">
            {t('SYSTEM_COMBINATION_LABEL', {
              index: combination.sequence_index,
              row: `${combination.row_size}`,
              defaultValue: `#${combination.sequence_index} · ${combination.row_size}/${combination.legs.length}`,
            })}
          </div>
          <div className="text-[11px] text-gray-500">
            {t('STAKE', 'Stake')}: {Number(combination.stake).toFixed(2)}{sym}
            {' · '}
            {t('TOTAL_ODD', 'Total odd')}: {printOdd(combination.combined_odd, oddsFormat)}
            {' · '}
            {t('POTENTIAL', 'Pot.')}: {Number(combination.potential_payout).toFixed(2)}{sym}
          </div>
        </div>
        <StatusBadge status={combination.status} />
      </div>
      <div className="mt-2 space-y-1">
        {combination.legs.map((tip) => (
          <TipRow key={`${combination.ticket_combination_id}-${tip.tip_id}`} tip={tip} currency={currency} oddsFormat={oddsFormat} />
        ))}
      </div>
    </div>
  );
}

function TicketCard({
  ticket,
  currency,
  oddsFormat,
  expandTicketId,
}: {
  ticket: RichTicket;
  currency: string;
  oddsFormat: string;
  expandTicketId?: number | null;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(() => expandTicketId != null && Number(ticket.ticket_id) === Number(expandTicketId));
  useEffect(() => {
    if (expandTicketId != null && Number(ticket.ticket_id) === Number(expandTicketId)) {
      setExpanded(true);
    }
  }, [expandTicketId, ticket.ticket_id]);
  const date = new Date(ticket.ticket_datetime);
  const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const sym = getCurrencySymbol(currency);
  const typeLabel =
    ticket.type === 'SYSTEM'
      ? t('SYSTEM', 'System')
      : ticket.type === 'M'
        ? t('MULTIPLE', 'Multiple')
        : t('SINGLE', 'Single');
  const systemSummary = systemRowsSummary(ticket.system_rows);
  const showSystemSummary = ticket.type === 'SYSTEM' && systemSummary;
  const soldPayoutLabel =
    ticket.type === 'SYSTEM'
      ? formatRange(Number(ticket.minimum_potential_payout), Number(ticket.maximum_potential_payout), sym)
      : `${Number(ticket.maximum_potential_payout).toFixed(2)}${sym}`;

  return (
    <div className="bg-white rounded-xl border border-border/60 shadow-sm overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-gray-50/60 transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <StatusBadge status={ticket.status} />
            <span className="text-[10px] text-gray-400 uppercase font-semibold">{typeLabel}</span>
          </div>
          <p className="text-xs text-gray-500">{dateStr}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            #{String(ticket.ticket_id)} · {ticket.tips.length} {ticket.tips.length === 1 ? t('TIP', 'tip') : t('TIPS', 'tips')}
          </p>
          {showSystemSummary && (
            <p className="text-[11px] text-gray-400 mt-0.5 truncate">
              {systemSummary}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500">{t('STAKE', 'Stake')}: {Number(ticket.bets).toFixed(2)}{sym}</p>
          {ticket.status === 'WIN' && ticket.wins > 0 && (
            <p className="text-sm font-bold text-emerald-600">+{Number(ticket.wins).toFixed(2)}{sym}</p>
          )}
          {ticket.status === 'SOLD' && (
            <p className="text-[11px] text-gray-400">
              {t('POTENTIAL', 'Pot.')}: {soldPayoutLabel}
            </p>
          )}
        </div>
        <i className={cn('bi text-gray-400 text-sm transition-transform', expanded ? 'bi-chevron-up' : 'bi-chevron-down')} />
      </button>

      {expanded && (
        <div className="px-4 pb-3 border-t border-border/40 bg-gray-50/40 space-y-3">
          {ticket.type === 'SYSTEM' && ticket.system_rows && ticket.system_rows.length > 0 && (
            <div className="pt-3">
              <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                <span>{t('SYSTEM_ROWS', 'System rows')}</span>
                <span>{ticket.system_rows.length}</span>
              </div>
              <div className="space-y-2">
                {ticket.system_rows.map((row) => (
                  <div key={row.size} className="rounded-lg border border-border/60 bg-white px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-foreground">{row.label}</span>
                      <span className="text-[11px] text-gray-500">
                        {row.combination_count} {t('LINES', 'lines')}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                      {t('STAKE', 'Stake')}: {Number(row.stake_per_line).toFixed(2)}{sym}
                      {' · '}
                      {t('POTENTIAL', 'Pot.')}: {formatRange(row.minimum_potential_payout, row.maximum_potential_payout, sym)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              {t('TIPS', 'tips')}
            </div>
            {ticket.tips.map((tip) => (
              <TipRow key={tip.tip_id} tip={tip} currency={currency} oddsFormat={oddsFormat} />
            ))}
          </div>

          {ticket.type === 'SYSTEM' && ticket.combinations && ticket.combinations.length > 0 && (
            <div className="pt-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                {t('SYSTEM_COMBINATIONS', 'System combinations')}
              </div>
              <div className="space-y-2">
                {ticket.combinations.map((combination) => (
                  <CombinationCard
                    key={combination.ticket_combination_id}
                    combination={combination}
                    currency={currency}
                    oddsFormat={oddsFormat}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MyBets() {
  const { t, i18n } = useTranslation();
  const [{ user }] = useUserSessionContext();
  const newlyPlacedTicketId = useTicketSlipStore((s) => s.newlyPlacedTicketId);
  const setNewlyPlacedTicketId = useTicketSlipStore((s) => s.setNewlyPlacedTicketId);
  const isGuest = isGuestUserSession(user);
  const { data: tickets, isLoading, isError } = useTicketsRichQuery({ enabled: !isGuest });
  const { data: creditData } = useCreditAvailableQuery({ enabled: !isGuest });
  const balance = creditData?.credit_available != null
    ? Number(creditData.credit_available)
    : Number(user?.player_balance ?? 0);
  const currency = (user?.currency as string) ?? 'USD';
  const oddsFormat = (user?.odds_format as string) ?? 'decimal';

  useEffect(() => {
    if (newlyPlacedTicketId != null && tickets?.some((tkt) => Number(tkt.ticket_id) === Number(newlyPlacedTicketId))) {
      setNewlyPlacedTicketId(null);
    }
  }, [newlyPlacedTicketId, tickets, setNewlyPlacedTicketId]);

  if (isGuest) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <i className="bi bi-receipt text-3xl text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">{t('LOGIN_TO_SEE_BETS', 'Log in to see your bets.')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-[var(--sport-color)] rounded-full animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <i className="bi bi-exclamation-triangle text-2xl text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">{t('ERROR_LOADING_BETS', 'Could not load your bets.')}</p>
      </div>
    );
  }

  if (!tickets || tickets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <i className="bi bi-receipt text-2xl text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-700 mb-1">{t('NO_BETS_YET', 'No bets yet')}</p>
        <p className="text-xs text-gray-400">{t('NO_BETS_HINT', 'Your placed bets will appear here.')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {!isGuest && (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50 border border-border/60">
          <span className="text-sm text-muted-foreground">{t('BALANCE', 'Balance')}</span>
          <span className="text-sm font-semibold text-foreground">{formatCurrency(balance, currency, i18n?.language as string)}</span>
        </div>
      )}
      <div className="space-y-2">
        {tickets.map((ticket) => (
          <TicketCard
            key={ticket.ticket_id}
            ticket={ticket}
            currency={currency}
            oddsFormat={oddsFormat}
            expandTicketId={newlyPlacedTicketId}
          />
        ))}
      </div>
    </div>
  );
}
